import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export const fetchCasesForUnits = createAsyncThunk(
  "deliveryUnits/fetchCases",
  async (
    {
      page = 1,
      limit = 25,
      search = "",
    }: {
      page?: number;
      limit?: number;
      search?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get(
        "/units/delivery-units/get-cases",
        {
          params: {
            page,
            limit,
            search,
          },
        }
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to fetch cases"
      );
    }
  }
);

export const fetchFzCasesForUnits = createAsyncThunk(
  "deliveryUnits/fetchFzCases",
  async (
    {
      page = 1,
      limit = 25,
      search = "",
    }: {
      page?: number;
      limit?: number;
      search?: string;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.get("/units/delivery-units/fz-cases", {
        params: { page, limit, search },
      });
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch FZ units cases"
      );
    }
  }
);

export const updateDeliveryUnits = createAsyncThunk(
  "deliveryUnits/updateDeliveryUnits",
  async (
    payload: {
      units_By: string;
      cases: {
        caseId: string;
        caseType: "live" | "redesign";
        newDeliveryUnits: number;
        designUnit?: number;
        designer?: string;
        captain?: string;
        patientName?: string | null;
        designToothNumbers?: string;
        deliveryToothNumbers?: string;
        filesBy?: string;
        filesUnits?: number;
        model?: boolean | string;
        modelType?: string;
        modelTypes?: string[];
        modelBy?: string;
        modelUnits?: number;
        designerUnits?: Record<string, number>;
        designerWork?: Record<string, "normal" | "model">;
        category?: string;
        crm?: string;
        unitsNotes?: string;
        redesignCategory?: string;
        internalCredits?: {
          case_id: string;
          unitFor: "Design" | "QC" | "Model";
          name: string;
          units: number;
        }[];
        internalRedesigns?: {
          case_id: string;
          designer?: string;
          captain?: string;
          designerUnits?: Record<string, number>;
          designerWork?: Record<string, "normal" | "model">;
          model?: boolean | string;
          modelTypes?: string[];
          modelBy?: string;
          modelUnits?: number;
        }[];
      }[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.put(
        "/units/delivery-units/update",
        payload
      );

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to update delivery units"
      );
    }
  }
);

export const extractUnitsCases = createAsyncThunk(
  "deliveryUnits/extractCases",
  async (
    { from, to }: { from: string; to: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await api.post(
        "/units/delivery-units/extract",
        { from, to }
      );
      return response.data.data as Record<string, unknown>[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message ||
          "Failed to extract cases"
      );
    }
  }
);

interface DeliveryUnitsState {
  cases: any[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

const initialState: DeliveryUnitsState = {
  cases: [],
  totalCount: 0,
  currentPage: 1,
  totalPages: 0,
  loading: false,
  error: null,
};

const deliveryUnitsSlice = createSlice({
  name: "deliveryUnits",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCasesForUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })

      .addCase(fetchCasesForUnits.fulfilled, (state, action) => {
        state.loading = false;

        state.cases = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })

      .addCase(fetchCasesForUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchFzCasesForUnits.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFzCasesForUnits.fulfilled, (state, action) => {
        state.loading = false;
        state.cases = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.currentPage;
        state.totalPages = action.payload.totalPages;
      })
      .addCase(fetchFzCasesForUnits.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(updateDeliveryUnits.fulfilled, (state, action) => {
        const updatedCases = action.payload;

        updatedCases.forEach((updated: any) => {
          state.cases = state.cases.filter(
            (c: any) => c.case_id !== updated.case_id
          );
        });

        state.totalCount = Math.max(
          0,
          state.totalCount - updatedCases.length
        );
      })

      .addCase(updateDeliveryUnits.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default deliveryUnitsSlice.reducer;